import { useMemo } from 'react';

import { Columns } from '@/components/table/types';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';
import {
  getSortableColumnsConfig,
  getTechnologyColumns,
} from '@/sections/technology/config';
import { Technology } from '@/types';

export interface UseTechnologyColumnsReturn {
  columns: Columns<Technology>;
  activeColumns: string[] | undefined;
  setActiveColumns: (newColumns: string[]) => void;
  placeholder: string;
  sortableColumnsConfig: ReturnType<typeof getSortableColumnsConfig>;
}

/**
 * Custom hook for managing technology table columns with smart positioning.
 *
 * Uses the shared useSmartColumnPositioning hook to ensure date columns
 * (created, visited) return to last positions when toggled on, while preserving
 * user drag-to-reorder preferences.
 *
 * Positioning Rules:
 * - Tags column → before date columns when toggled on
 * - Date columns (created, visited) → last positions when toggled on
 * - User drag-to-reorder preferences are preserved
 * - Smart positioning only applies when columns are toggled on, NOT when dragged
 *
 * @returns Column management interface with smart positioning
 */
export function useTechnologyColumns(): UseTechnologyColumnsReturn {
  // Initialize default column definitions
  const defaultColumns: Columns<Technology> = useMemo(
    () => getTechnologyColumns(),
    []
  );

  // Generate sortable columns configuration
  const sortableColumnsConfig = useMemo(() => getSortableColumnsConfig(), []);

  // Initialize smart column positioning with positioning rules
  const { activeColumns, setActiveColumns, columns } =
    useSmartColumnPositioning({
      key: 'technologies',
      defaultColumns,
      defaultConfig: sortableColumnsConfig,
      positioningRules: {
        // Technology table has NO status column (unlike Assets/Vulnerabilities)
        beforeLastColumns: ['tags'], // Tags → before date columns when toggled on
        lastColumns: ['created', 'visited'], // Date columns → last when toggled on
      },
    });

  // Generate enhanced placeholder showing active/total column counts
  const placeholder = useMemo(() => {
    const total =
      sortableColumnsConfig.active.length +
      sortableColumnsConfig.available.length;
    const active = activeColumns?.length ?? 0;
    return `Columns (${active}/${total})`;
  }, [
    activeColumns?.length,
    sortableColumnsConfig.active.length,
    sortableColumnsConfig.available.length,
  ]);

  return {
    columns,
    activeColumns,
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  };
}
